import type { AnyAction, Middleware, MiddlewareAPI } from "@reduxjs/toolkit"

import defaultStore, { type RootState } from "../store"

import {
  CACHEABLE,
  type CacheableEntry,
  clearBootstrapCache,
  findCacheableByFulfilledType,
  findCacheableByRefreshTriggerType,
  readBootstrapCache,
  writeBootstrapCacheEntry,
} from "./bootstrapCache"

/**
 * Cross-tab bootstrap cache (UDC-4323) — HOW it wires into the app.
 *
 * Pipeline:
 *  1. middleware — persists every real fetch's payload to the cache + notifies
 *                  other tabs.
 *  2. hydrate    — on boot, seeds Redux from the cache (a warm tab renders
 *                  without re-fetching).
 *  3. sync       — other tabs' updates are re-hydrated here via BroadcastChannel.
 *
 * Kept fresh two ways: normal navigation re-caches a fetched dataset, or a
 * `refreshedBy` write thunk (bootstrapCache.ts) triggers an auto re-fetch on
 * fulfilling. Deliberately NO window-focus revalidation — it re-fired the
 * bootstrap thunks at interaction time and raised the global loading backdrop,
 * which blocked clicks (e.g. "Add patient").
 */

const readUser = (store: typeof defaultStore) =>
  (store.getState() as RootState).userService.user

// ---------------------------------------------------------------------------
// Cross-tab channel (no-op if BroadcastChannel is unavailable)
// ---------------------------------------------------------------------------

type ChannelMessage = { type: "update"; key: string } | { type: "invalidate" }

const CHANNEL_NAME = "udc-bootstrap"
let channel: BroadcastChannel | null = null

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === "undefined") return null
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME)
  return channel
}

const postMessage = (message: ChannelMessage): void => {
  try {
    getChannel()?.postMessage(message)
  } catch {
    // best-effort
  }
}

// ---------------------------------------------------------------------------
// 1. Caching middleware
// ---------------------------------------------------------------------------

// Hydration/sync dispatches carry this requestId prefix; the middleware ignores
// them so seeding cached data can't re-cache or re-broadcast (which would loop).
// The trailing ":" keeps the startsWith check unambiguous vs a real RTK requestId.
const BOOTSTRAP_REQUEST_ID_PREFIX = "bootstrap-cache:"

// requestId → org the fetch was made for.
const requestOrgIds = new Map<string, string | undefined>()

// Cached thunks take no argument.
const refetch = (store: MiddlewareAPI, entry: CacheableEntry): void => {
  const dispatch = store.dispatch as typeof defaultStore.dispatch
  dispatch(entry.thunk(undefined))
}

export const bootstrapCacheMiddleware: Middleware =
  (store) => (next) => (action: AnyAction) => {
    const result = next(action)

    if (typeof action?.type === "string") {
      const entry = findCacheableByFulfilledType(action.type)
      const requestId = action.meta?.requestId as string | undefined
      const isBootstrapDispatch =
        requestId?.startsWith(BOOTSTRAP_REQUEST_ID_PREFIX) ?? false

      if (requestId && !isBootstrapDispatch) {
        if (CACHEABLE.some((item) => item.thunk.pending.type === action.type)) {
          requestOrgIds.set(
            requestId,
            readUser(store as typeof defaultStore)?.current_orgId,
          )
        } else if (
          CACHEABLE.some((item) => item.thunk.rejected.type === action.type)
        ) {
          requestOrgIds.delete(requestId)
        }
      }

      // Persist + broadcast genuine fetches only — not our own hydration/sync.
      if (entry && !isBootstrapDispatch) {
        const user = readUser(store as typeof defaultStore)
        const requestOrgId = requestId ? requestOrgIds.get(requestId) : undefined
        if (requestId) requestOrgIds.delete(requestId)
        // project (if defined) trims the payload so only a safe subset is stored.
        const value = entry.project
          ? entry.project(action.payload)
          : action.payload
        writeBootstrapCacheEntry(
          user?.user_id,
          requestOrgId ?? user?.current_orgId,
          entry.key,
          value,
        )
        postMessage({ type: "update", key: entry.key })
        // Org switched mid-flight - refetch for the current org.
        if (requestOrgId && requestOrgId !== user?.current_orgId) {
          refetch(store, entry)
        }
      }

      // A write thunk just fulfilled - re-fetch its paired read thunk so the
      // cache doesn't go stale. Runs after next(action), so the write's own
      // reducer has already applied.
      const refreshEntry = findCacheableByRefreshTriggerType(action.type)
      if (refreshEntry && !isBootstrapDispatch) {
        const readPayload = refreshEntry.fromWriteResponse?.(action.payload)
        if (readPayload === undefined) {
          refetch(store, refreshEntry)
        } else {
          store.dispatch(
            refreshEntry.thunk.fulfilled(
              readPayload as never,
              `write-response:${requestId ?? ""}`,
              undefined as never,
            ),
          )
        }
      }
    }

    return result
  }

// ---------------------------------------------------------------------------
// 2. Hydration (seed Redux from cache)
// ---------------------------------------------------------------------------

// The user+org identity we've already hydrated for (null = not yet hydrated).
let hydratedIdentity: string | null = null

const identityOf = (user: ReturnType<typeof readUser>): string =>
  `${user?.user_id}.${user?.current_orgId}`

// Hydrated for the current org (not just any org).
export const isBootstrapHydrated = (): boolean =>
  hydratedIdentity !== null &&
  hydratedIdentity === identityOf(readUser(defaultStore))

const resetHydration = (): void => {
  hydratedIdentity = null
}

// Re-dispatch a cached payload through its thunk's `fulfilled` so each slice's
// real reducer applies it. The bootstrap-* requestId keeps the middleware from
// re-caching/re-broadcasting it.
const seed = (store: typeof defaultStore, entry: CacheableEntry, payload: unknown) => {
  store.dispatch(
    entry.thunk.fulfilled(
      payload as never,
      `${BOOTSTRAP_REQUEST_ID_PREFIX}hydrate`,
      undefined as never,
    ),
  )
}

/**
 * Hydrate the store from cache for the current user+org. Idempotent per identity.
 * Returns true if anything was seeded (or was already hydrated this identity).
 */
export const hydrateBootstrapCache = (
  store: typeof defaultStore = defaultStore,
): boolean => {
  const user = readUser(store)
  if (!user?.user_id || !user?.current_orgId) return false

  const identity = identityOf(user)
  if (hydratedIdentity === identity) return true

  const blob = readBootstrapCache(user.user_id, user.current_orgId)
  if (!blob) return false

  let didHydrate = false
  CACHEABLE.forEach((entry) => {
    if (blob[entry.key] === undefined) return
    seed(store, entry, blob[entry.key])
    didHydrate = true
  })

  if (didHydrate) hydratedIdentity = identity
  return didHydrate
}

// Re-seed a single dataset (used when another tab reports it refreshed).
const rehydrateEntry = (store: typeof defaultStore, key: string): void => {
  const user = readUser(store)
  const blob = readBootstrapCache(user?.user_id, user?.current_orgId)
  if (!blob || blob[key] === undefined) return
  const entry = CACHEABLE.find((item) => item.key === key)
  if (entry) seed(store, entry, blob[key])
}

// ---------------------------------------------------------------------------
// 3. Cross-tab sync
// ---------------------------------------------------------------------------

let installed = false

/**
 * Install the cross-tab sync listener. Idempotent (app-lifetime). When another
 * tab persists fresh data it broadcasts `update` and we re-hydrate just that
 * dataset, so open tabs converge without a reload; `invalidate` (logout/org-
 * switch) drops the hydration marker. Re-hydration is non-blocking (it dispatches
 * `fulfilled`, never `pending`), so it never raises the global backdrop.
 */
export const installBootstrapSync = (
  store: typeof defaultStore = defaultStore,
): void => {
  if (installed) return
  installed = true

  const ch = getChannel()
  ch?.addEventListener("message", (event: MessageEvent) => {
    const message = event.data as ChannelMessage
    if (message.type === "update") rehydrateEntry(store, message.key)
    else if (message.type === "invalidate") resetHydration()
  })
}

// ---------------------------------------------------------------------------
// Invalidation (logout / org-switch)
// ---------------------------------------------------------------------------

/** Wipe the cache, reset hydration, and tell other tabs to drop theirs. */
export const invalidateBootstrapCache = (): void => {
  clearBootstrapCache()
  resetHydration()
  postMessage({ type: "invalidate" })
}
